import { Injectable } from '@angular/core';

/** A dialer call that ended in this browser tab. */
export interface LastPatientCall {
  /** Twilio PARENT call SID; also the Firestore id at calls/{nurseUid}/userCalls/{callSid}. */
  callSid: string;
  patientId: string;
  nurseUid: string;
  /** Epoch milliseconds. */
  endedAt: number;
}

/**
 * Remembers the most recent call each nurse placed to each patient from the call dialog, so the
 * clinical time form can link a "spoke with the patient" entry to the call behind it.
 *
 * In memory only: a page reload forgets it, and the short-call check then falls back to the nurse's
 * own call list. Holds ids and a timestamp, nothing else.
 */
@Injectable({ providedIn: 'root' })
export class LastPatientCallService {
  private readonly calls = new Map<string, LastPatientCall>();

  record(call: LastPatientCall): void {
    if (!call?.callSid || !call.patientId || !call.nurseUid) {
      return;
    }
    this.calls.set(this.key(call.nurseUid, call.patientId), { ...call });
  }

  /** The latest call by this nurse to this patient that ended within `windowMs`, or null. */
  getRecent(patientId: string, nurseUid: string, windowMs: number, now: number = Date.now()): LastPatientCall | null {
    if (!patientId || !nurseUid) {
      return null;
    }
    const call = this.calls.get(this.key(nurseUid, patientId));
    if (!call || now - call.endedAt > windowMs) {
      return null;
    }
    return { ...call };
  }

  private key(nurseUid: string, patientId: string): string {
    return `${nurseUid}::${patientId}`;
  }
}
